import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearch as useRouteSearch } from '@tanstack/react-router';
import {
  Folder as FolderIcon,
  Hash,
  History,
  Search as SearchIcon,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import { AssetGrid } from '@/components/AssetGrid';
import { EmptyState, ErrorState, CenteredSpinner, Button } from '@/components/ui';
import { cn } from '@/lib/format';
import { useFacets, useFolders, useMe, useSearch, useSuggest } from '@/lib/queries';
import {
  clearRecentSearches, loadLastSearch, loadRecentSearches, pushRecentSearch, saveLastSearch, sameSearch,
  type RecentSearch,
} from '@/lib/recent-searches';
import { t } from '@/lib/i18n';
import type { FacetValue } from '@shared/types';

/**
 * Meta-tag / AI search — the replacement for Pimcore's custom "Meta Tag Search"
 * grid, whose shape this deliberately mirrors: one filter box per column, all
 * independent and AND-ed together, so "brand=clipper + hashtags=organic"
 * narrows. A single box scoped to one field could never express that.
 *
 * Hashtags are a plain substring box, not a picker: the library holds ~2,400
 * distinct AI-invented tags (Teapot, Ladybug, sustainablepackaging), so there is
 * no vocabulary small enough to choose from.
 *
 * Seeded from URL search params, so the rail's brand/type facet links still land
 * here pre-filtered.
 */

/** Free-text column filters: URL param ↔ label. */
const textFilters = () => [
  { key: 'brand', label: t('searchPage.brand'), placeholder: t('searchPage.anyBrand') },
  { key: 'productTitle', label: t('searchPage.productTitle'), placeholder: t('searchPage.anyTitle') },
  { key: 'fileName', label: t('searchPage.fileName'), placeholder: t('searchPage.anyFileName') },
  { key: 'path', label: t('searchPage.path'), placeholder: t('searchPage.anyFolder') },
  { key: 'description', label: t('searchPage.description'), placeholder: t('searchPage.anyDescription') },
  { key: 'hashtag', label: t('searchPage.hashtags'), placeholder: t('searchPage.anyHashtag') },
] as const;

type TextKey = ReturnType<typeof textFilters>[number]['key'];
type Filters = Record<TextKey, string> & {
  q: string;
  imageTypes: string[];
  fileTypes: string[];
  location: string;
};

const EMPTY: Filters = {
  q: '', brand: '', productTitle: '', fileName: '', path: '', description: '', hashtag: '',
  imageTypes: [], fileTypes: [], location: '',
};

export function SearchPage() {
  const routeSearch = useRouteSearch({ strict: false }) as Partial<Record<string, string>>;

  const fromUrl = useMemo<Filters>(
    () => ({
      ...EMPTY,
      q: routeSearch.q ?? '',
      brand: routeSearch.brand ?? '',
      productTitle: routeSearch.productTitle ?? '',
      fileName: routeSearch.fileName ?? '',
      path: routeSearch.path ?? '',
      description: routeSearch.description ?? '',
      hashtag: routeSearch.hashtag ?? '',
      location: routeSearch.location ?? '',
      // The rail links with a single `imageType`; treat it as a one-item selection.
      imageTypes: routeSearch.imageType ? [routeSearch.imageType] : [],
    }),
    [routeSearch.q, routeSearch.brand, routeSearch.productTitle, routeSearch.fileName,
     routeSearch.path, routeSearch.description, routeSearch.hashtag, routeSearch.location,
     routeSearch.imageType],
  );

  // Arriving without URL filters (back from an asset or a folder) restores this
  // tab's last search; its results are still in the query cache, so nothing reruns.
  const [initial] = useState<Filters>(() => {
    if (!sameSearch(fromUrl, EMPTY)) return fromUrl;
    const last = loadLastSearch();
    return last ? { ...EMPTY, ...last } as Filters : fromUrl;
  });
  const [filters, setFilters] = useState<Filters>(initial);
  // Every text box is free text, so the query is debounced as a whole rather
  // than firing on each keystroke across six inputs.
  const [applied, setApplied] = useState<Filters>(initial);

  // Re-seed when a rail facet link changes the URL — but not on mount, which
  // would wipe the restored search straight away.
  const seededFrom = useRef(fromUrl);
  useEffect(() => {
    if (seededFrom.current === fromUrl) return;
    seededFrom.current = fromUrl;
    setFilters(fromUrl);
    setApplied(fromUrl);
  }, [fromUrl]);

  useEffect(() => saveLastSearch(applied), [applied]);

  useEffect(() => {
    const id = setTimeout(() => setApplied(filters), 250);
    return () => clearTimeout(id);
  }, [filters]);

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const toggleMulti = (key: 'imageTypes' | 'fileTypes', value: string) =>
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  // A two-character floor on free text only; any other filter counts on its own.
  const hasCriteria =
    applied.q.trim().length >= 2 ||
    textFilters().some(({ key }) => applied[key].trim() !== '') ||
    applied.imageTypes.length > 0 ||
    applied.fileTypes.length > 0 ||
    applied.location !== '';

  const activeCount =
    textFilters().filter(({ key }) => applied[key].trim() !== '').length +
    applied.imageTypes.length + applied.fileTypes.length +
    (applied.location ? 1 : 0) + (applied.q.trim() ? 1 : 0);

  // One filter object for both the results and the facet counts, so the pills
  // can never describe a different query than the one that ran.
  const criteria = useMemo(
    () => ({
      q: applied.q.trim() || undefined,
      brand: applied.brand.trim() || undefined,
      productTitle: applied.productTitle.trim() || undefined,
      description: applied.description.trim() || undefined,
      fileName: applied.fileName.trim() || undefined,
      path: applied.path.trim() || undefined,
      hashtag: applied.hashtag.trim() || undefined,
      imageTypes: applied.imageTypes.length ? applied.imageTypes : undefined,
      fileTypes: applied.fileTypes.length ? applied.fileTypes : undefined,
      location: applied.location || undefined,
    }),
    [applied],
  );

  const search = useSearch({ ...criteria, perPage: 200 }, hasCriteria);

  // Recent searches, per user. A search is kept once it has settled with results
  // for a moment, so a half-typed query never lands in the list.
  const userId = useMe().data?.id;
  const [recent, setRecent] = useState<RecentSearch[]>([]);
  useEffect(() => { if (userId) setRecent(loadRecentSearches(userId)); }, [userId]);
  const total = search.data?.total;
  useEffect(() => {
    if (!userId || !hasCriteria || !total) return;
    const id = setTimeout(() => setRecent(pushRecentSearch(userId, applied, total)), 1500);
    return () => clearTimeout(id);
  }, [userId, hasCriteria, applied, total]);

  // Facets follow the same criteria, so each pill's count reflects the other
  // active filters rather than the whole library.
  const { data: facets } = useFacets(criteria);
  // The native backend has no country folders, so it reports none — hide the
  // dropdown entirely rather than offering a filter that can only say "All".
  const locations = facets?.locations ?? [];

  return (
    <div className="flex h-full flex-col">
      <header className="space-y-3 border-b line surface-card px-5 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-brand-500" />
          <h1 className="text-base font-semibold text-strong">{t('searchPage.aiSearch')}</h1>
          {activeCount > 0 && (
            <Button
              variant="secondary"
              className="ml-auto h-7 text-xs"
              onClick={() => { setFilters(EMPTY); setApplied(EMPTY); }}
            >
              <X className="size-3.5" /> {activeCount === 1
                ? t('searchPage.clearOneFilter', { n: activeCount })
                : t('searchPage.clearFilters', { n: activeCount })}
            </Button>
          )}
        </div>

        {recent.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted">
              <History className="size-3.5" /> {t('searchPage.recentSearches')}
            </span>
            {recent.map((r) => (
              <button
                key={r.at}
                type="button"
                onClick={() => { const f = { ...EMPTY, ...r.filters } as Filters; setFilters(f); setApplied(f); }}
                className={cn(
                  'max-w-[22rem] truncate rounded-full border line px-2.5 py-0.5 text-xs text-strong hover:border-brand-500',
                  sameSearch(r.filters, applied) && 'border-brand-500 bg-brand-500/10',
                )}
                title={describeSearch(r.filters)}
              >
                {describeSearch(r.filters)} <span className="text-muted tabular-nums">· {r.total}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => { if (userId) clearRecentSearches(userId); setRecent([]); }}
              className="text-[11px] text-muted underline-offset-2 hover:underline"
            >
              {t('searchPage.clearRecent')}
            </button>
          </div>
        )}

        {/* Everything-box: still the fastest way in when you don't care which
            field matched. Combines with the per-column filters below. */}
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
          <input
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
            autoFocus
            placeholder={t('searchPage.searchAllFields')}
            className="w-full rounded-lg border line surface-card py-2 pl-9 pr-3 text-sm text-strong placeholder:text-faint focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        {/* Per-column filters, as the old grid had them. */}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {textFilters().map(({ key, label, placeholder }) =>
            key === 'path' ? (
              // Path completes against the folder tree, which is already loaded.
              <PathFilter
                key={key}
                label={label}
                placeholder={placeholder}
                value={filters.path}
                onChange={(v) => set('path', v)}
              />
            ) : key === 'hashtag' ? (
              // Hashtags complete against the server: ~2,400 distinct AI tags is
              // far too many to ship to the client as a list.
              <HashtagFilter
                key={key}
                label={label}
                placeholder={placeholder}
                value={filters.hashtag}
                onChange={(v) => set('hashtag', v.replace(/^#/, ''))}
              />
            ) : (
              <label key={key} className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</span>
                <input
                  value={filters[key]}
                  // The "#" stripping that used to live here moved into
                  // HashtagFilter, which now owns that field.
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={placeholder}
                  className="rounded border line surface-card px-2 py-1 text-xs text-strong placeholder:text-faint focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
            ),
          )}
        </div>

        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          <MultiSelect
            label={t('searchPage.imageType')}
            options={facets?.imageTypes ?? []}
            selected={filters.imageTypes}
            onToggle={(v) => toggleMulti('imageTypes', v)}
            limit={12}
          />
          <MultiSelect
            label={t('searchPage.fileType')}
            options={facets?.fileTypes ?? []}
            selected={filters.fileTypes}
            onToggle={(v) => toggleMulti('fileTypes', v)}
            limit={8}
            labelFor={fileTypeLabel}
          />
          {locations.length > 0 && (
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('searchPage.location')}</span>
              <select
                value={filters.location}
                onChange={(e) => set('location', e.target.value)}
                className="rounded border line surface-card px-2 py-1 text-xs text-strong"
              >
                <option value="">{t('searchPage.allLocations')}</option>
                {locations.map((l) => (
                  <option key={l.name} value={l.name}>{l.name} ({l.count})</option>
                ))}
              </select>
            </label>
          )}
          {search.data && (
            <span className="ml-auto self-end text-xs text-muted tabular-nums">{t('searchPage.results', { n: search.data.total })}</span>
          )}
        </div>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {!hasCriteria ? (
          <EmptyState
            icon={<SearchIcon />}
            title={t('searchPage.searchAiTags')}
            description={t('searchPage.searchAiTagsDescription')}
          />
        ) : search.isPending ? (
          <CenteredSpinner />
        ) : search.error ? (
          <ErrorState error={search.error} onRetry={() => void search.refetch()} />
        ) : (search.data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={<SearchIcon />} title={t('searchPage.noResults')} description={t('searchPage.noResultsDescription')} />
        ) : (
          <AssetGrid assets={search.data!.items} newTab />
        )}
      </div>
    </div>
  );
}

/** One line for a recent-search chip: the free text first, then each filter. */
function describeSearch(f: Record<string, string | string[]>): string {
  const parts: string[] = [];
  if (typeof f.q === 'string' && f.q.trim()) parts.push(`"${f.q.trim()}"`);
  for (const { key, label } of textFilters()) {
    const v = f[key];
    if (typeof v === 'string' && v.trim()) parts.push(`${label}: ${v.trim()}`);
  }
  for (const k of ['imageTypes', 'fileTypes'] as const) {
    const v = f[k];
    if (Array.isArray(v) && v.length) parts.push(v.map((x) => (k === 'fileTypes' ? fileTypeLabel(x) : x)).join(', '));
  }
  if (typeof f.location === 'string' && f.location) parts.push(f.location);
  return parts.join(' · ');
}

/**
 * Toggle chips rather than a <select multiple>: both vocabularies are tiny
 * (~7 image types, 5 file types) and multi-select boxes are awkward to operate.
 */
/** File types arrive as mime subtypes; show the names people know. */
const FILE_TYPE_NAMES: Record<string, string> = {
  'vnd.adobe.photoshop': 'PSD',
  'svg+xml': 'SVG',
  'x-eps': 'EPS',
  postscript: 'PostScript',
  jpeg: 'JPG',
};
const fileTypeLabel = (v: string) => FILE_TYPE_NAMES[v] ?? v.replace(/^x-/, '').toUpperCase();

function MultiSelect({
  label, options, selected, onToggle, limit, labelFor = (v) => v,
}: {
  label: string;
  options: FacetValue[];
  selected: string[];
  onToggle: (value: string) => void;
  /** Show only the biggest N until "more" is clicked; the AI tags have a long tail of one-offs. */
  limit?: number;
  labelFor?: (value: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  // Biggest first, so the useful values lead and the one-off AI spellings trail.
  // A selected value whose count has dropped to zero must still be rendered, or
  // it would vanish the moment it was clicked and leave no way to unselect it.
  const sorted = useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const missing = selected.filter((v) => !known.has(v)).map((value) => ({ value, count: 0 }));
    return [...options, ...missing].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  }, [options, selected]);
  const hidden = limit && !expanded ? Math.max(0, sorted.length - limit) : 0;
  // Selected values always show, even when they sit below the cut.
  const shown = hidden ? sorted.filter((o, i) => i < limit! || selected.includes(o.value)) : sorted;

  if (sorted.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</span>
      <div className="flex flex-wrap gap-1">
        {shown.map(({ value: opt, count }) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => onToggle(opt)}
              aria-pressed={on}
              title={count === 1
                ? t('searchPage.oneAsset', { n: count.toLocaleString() })
                : t('searchPage.nAssets', { n: count.toLocaleString() })}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
                on
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : 'line surface-card text-muted hover:text-strong',
              )}
            >
              {labelFor(opt)}
              {/* Dimmed rather than hidden at zero: "0" is the useful signal
                  here — it says the filter exists but would return nothing. */}
              <span className={cn('tabular-nums', on ? 'text-white/70' : 'text-faint')}>
                {count.toLocaleString()}
              </span>
            </button>
          );
        })}
        {limit != null && sorted.length > limit && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="rounded-full px-2 py-0.5 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            {expanded ? t('searchPage.showLess') : t('searchPage.moreCount', { n: (sorted.length - limit).toLocaleString() })}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Shared shell for the two completing filters.
 *
 * Both stay free-text boxes: every filter here is a substring match, so a
 * partial fragment is a legitimate query that no suggestion equals. The list
 * narrows what you type rather than restricting it — picking a suggestion is a
 * shortcut, never a requirement.
 *
 * Keyboard handling mirrors the rail's type-ahead: arrows wrap, Escape closes,
 * and Enter is only intercepted while an option is highlighted so typing a
 * fragment and hitting Enter still runs that search.
 */
function AutocompleteFilter({
  label,
  placeholder,
  value,
  onChange,
  options,
  listId,
  icon: Icon,
  loading,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  /** Already filtered and capped by the caller. */
  options: { key: string; text: string; hint?: string }[];
  listId: string;
  icon: LucideIcon;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  // A changed list invalidates the highlight — index 3 of the old list is not
  // index 3 of the new one.
  useEffect(() => setActive(-1), [options]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function pick(text: string) {
    onChange(text);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Enter' && open && active >= 0 && options[active]) {
      e.preventDefault();
      pick(options[active].text);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!options.length) return;
      e.preventDefault(); // stop the caret jumping to either end of the input
      setOpen(true);
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return next < 0 ? options.length - 1 : next >= options.length ? 0 : next;
      });
    }
  }

  const showList = open && options.length > 0;

  return (
    <div ref={boxRef} className="relative flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</span>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        autoComplete="off"
        className="rounded border line surface-card px-2 py-1 text-xs text-strong placeholder:text-faint focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border line surface-card py-1 shadow-lg scroll-thin"
        >
          {loading && <li className="px-2 py-1 text-[11px] text-faint">{t('searchPage.searching')}</li>}
          {options.map((o, i) => (
            <li key={o.key}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                // mousedown, not click: the input's blur would close the list
                // before a click ever landed.
                onMouseDown={(e) => { e.preventDefault(); pick(o.text); }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs',
                  i === active ? 'surface-hover text-strong' : 'text-body',
                )}
              >
                <Icon className="size-3 shrink-0 text-faint" />
                <span className="truncate">{o.text}</span>
                {o.hint && <span className="ml-auto shrink-0 tabular-nums text-[10px] text-faint">{o.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Path filter, completing against the folder tree.
 *
 * Uses `useFolders` rather than /search/suggest: it is already fetched and
 * cached for the sidebar, is permission scoped by the same rule, and holds
 * every folder — so matching is instant on each keystroke with no request, no
 * debounce and no rate limit.
 */
function PathFilter(props: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { data } = useFolders();

  const options = useMemo(() => {
    const all = data?.items ?? [];
    const q = props.value.trim().toLowerCase();
    // An empty box offers the shallowest folders — the useful starting points —
    // rather than an arbitrary alphabetical slice of 1,400 deep paths.
    const ranked = q
      ? all.filter((f) => f.path.toLowerCase().includes(q))
      : [...all].sort((a, b) => a.path.split('/').length - b.path.split('/').length);
    return ranked.slice(0, 8).map((f) => ({ key: String(f.id), text: f.path }));
  }, [data, props.value]);

  return <AutocompleteFilter {...props} options={options} listId="path-suggestions" icon={FolderIcon} />;
}

/**
 * Hashtag filter, completing against /search/suggest.
 *
 * Unlike folders there is no client-side list to match against — the library
 * holds ~2,400 distinct AI-invented tags — so this queries the same debounced,
 * permission-scoped endpoint the rail's type-ahead uses and keeps only the tag
 * suggestions.
 */
function HashtagFilter(props: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [debounced, setDebounced] = useState(props.value);

  // The endpoint is rate limited and fans out across several queries, so it
  // should see typing pauses rather than keystrokes.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(props.value), 200);
    return () => clearTimeout(t);
  }, [props.value]);

  const { data = [], isFetching } = useSuggest(debounced);

  const options = useMemo(
    () =>
      data
        .filter((s) => s.kind === 'tag')
        .slice(0, 8)
        .map((s) => ({ key: s.value, text: s.value, hint: s.count.toLocaleString() })),
    [data],
  );

  return (
    <AutocompleteFilter
      {...props}
      options={options}
      listId="hashtag-suggestions"
      icon={Hash}
      loading={isFetching && options.length === 0}
    />
  );
}
